import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
/* `Tabs` de components/ui/tabs ya no se usa: el template arma las pestañas con
   `YoolTabs variant="underline"`, que es controlado (value/options/onChange) y
   no monta un TabsContent por pestaña. La pestaña activa vive en el estado de
   este componente y el cuerpo se elige con un switch. */
import {
  PageHead, FormCard, Section, Field,
  ActionBar, Callout,
} from "../../components/product_config/formkit";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { useSupabaseClient } from "../../lib/supabase";
import { logError } from "../../lib/utils";
import { obtener, crear, actualizar } from "../../services/historiasApi";
import {
  CampoTexto, CampoArea, CampoSelect, CampoNumero, CampoFecha, CampoColor,
  CampoImagen, CampoResaltados, CampoSwitch, CampoPills, CampoRadioCards,
  Boton, Pila, Fila, TarjetaRepetidor,
} from "./campos";
import Previsualizacion from "./Previsualizacion";
/* Las vistas por pestaña (`VistaPortada`, `VistaCuerpo`, …) ya no se montan
   acá. Antes cada pestaña llevaba su propia `Section "Previsualización"` y
   además existía una pestaña `Previsualizar` que las recomponía todas: seis
   lugares mostrando lo mismo. Con el panel lateral queda UNO, y `Previsualizacion`
   sigue siendo quien las compone. */
import {
  formularioVacio, desdeHistoria, haciaEntrada, slugDesdeNombre,
  opcionesDeTiempo,
  COLORES_FANCY, TIPOS_BLOQUE, CATEGORIAS,
} from "./historiaFormModel";

/**
 * Cuerpo de una pestaña: se dibuja solo cuando es la activa. `YoolTabs` es
 * controlado y no monta paneles, así que la condición vive acá.
 *
 * VA FUERA DEL COMPONENTE A PROPOSITO. Definido adentro sería un tipo de
 * componente NUEVO en cada render, y React desmontaría y volvería a montar el
 * cuerpo entero de la pestaña en cada tecla: el campo perdería el foco letra
 * por letra. Definido acá la identidad es estable y el subárbol se conserva.
 */
function Panel({ value, tab, children }) {
  return tab === value ? <>{children}</> : null;
}

/** Contenedor del cuerpo: `YoolCard` del sistema, o `FormCard` sin bundle.
    Fuera del componente por el mismo motivo que `Panel`. */
function Tarjeta({ card: YoolCard, pad = 32, children }) {
  if (YoolCard) return <YoolCard radius="xl" pad={pad}>{children}</YoolCard>;
  return <FormCard>{children}</FormCard>;
}

export default function HistoriasForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const supabase = useSupabaseClient();
  const editando = Boolean(id);

  const [form, setForm] = useState(formularioVacio);
  const [productos, setProductos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  /* Confirmación de publicar sin producto principal. Ver `guardar`. */
  const [avisoPrincipalAbierto, setAvisoPrincipalAbierto] = useState(false);
  // La pestaña activa es estado propio: `YoolTabs` es controlado, no monta un
  // panel por pestaña como el `Tabs` que había antes.
  const [tab, setTab] = useState("historia");
  // El panel arranca abierto, igual que en el template (`previewAbierto: true`).
  // Es lo que hace que la previsualización se mire mientras se escribe y no
  // como paso aparte al final.
  const [previewAbierto, setPreviewAbierto] = useState(true);
  // C1/I1: mientras no sepamos que la historia real cargo bien, el formulario
  // no se muestra editable. Arranca en `true` solo cuando hay algo que cargar
  // (editando); una historia nueva no espera nada.
  const [cargando, setCargando] = useState(editando);
  const [errorCarga, setErrorCarga] = useState(null);
  // Foto del formulario tal como quedo al cargar (o al abrir uno nuevo). Sirve
  // para decir "cambios sin guardar" con un hecho y no con una corazonada: se
  // compara la entrada que se mandaria hoy contra la que se mandaria entonces.
  const [inicial, setInicial] = useState(() => JSON.stringify(haciaEntrada(formularioVacio())));

  // El catalogo se LEE con la clave publishable (lectura, no escritura). I4:
  // se trae activo/archivado para poder excluir archivados de las opciones
  // pero conservar un inactivo que la historia ya tenia elegido.
  useEffect(() => {
    supabase
      .from("producto")
      .select("id, nombre, yoolbrand_slug, activo, archivado")
      .order("nombre")
      .then(({ data, error }) => {
        if (error) {
          // I3: antes se comia el error en silencio y la pestana Productos
          // quedaba con el desplegable vacio sin ninguna explicacion.
          logError("No se pudo cargar el catalogo de productos:", error);
          toast.error(
            "No se pudo cargar el catalogo de productos. La pestaña Productos puede no mostrar todas las opciones."
          );
          return;
        }
        setProductos(data || []);
      });
  }, [supabase]);

  useEffect(() => {
    if (!editando) return;
    let cancelado = false;
    (async () => {
      try {
        const historia = await obtener(await getToken(), id);
        if (cancelado) return;
        const cargado = desdeHistoria(historia);
        setForm(cargado);
        setInicial(JSON.stringify(haciaEntrada(cargado)));
        setCargando(false);
      } catch (e) {
        if (cancelado) return;
        // C1: si la carga falla, el formulario NO se muestra editable con
        // los valores por defecto — eso permitia guardar encima de la
        // historia real con cuerpo vacio, sin productos y en borrador.
        // Cubre tambien I1: como el formulario no se renderiza hasta que
        // `cargando` sea false, no hay ninguna ventana en la que una
        // respuesta tardia pueda pisar lo que el editor ya tipeo.
        setErrorCarga(e.message);
        setCargando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [editando, id, getToken]);

  const set = (campo) => (e) =>
    setForm((f) => ({ ...f, [campo]: e?.target ? e.target.value : e }));

  const sinGuardar = JSON.stringify(haciaEntrada(form)) !== inicial;
  const DS = (typeof window !== "undefined" && window.DesignSystemRemix_1a4bd3) || {};
  const {
    YoolActionBar, YoolTabs, YoolCard, YoolPageHeader, YoolBadge, StatusPill,
  } = DS;

  /* Encabezado del detalle, verbatim del template (`formTitulo`,
     `formEyebrow`, `formSlugUrl`). El título es el NOMBRE de la historia, no
     "Editar historia": en un back office donde se abren varias seguidas, el
     encabezado tiene que decir cuál está abierta. El eyebrow carga el estado y
     el subtítulo la URL pública, que es lo que el slug significa. */
  const formTitulo = form.nombre || "Nueva historia";
  const formEyebrow = editando
    ? form.estado === "publicada"
      ? "Editorial · Publicada"
      : "Editorial · Borrador"
    : "Editorial · Nueva";
  const formSlugUrl = form.slug
    ? `/historias/${form.slug}`
    : "El slug define la URL pública";
  const pillEstado = form.estado === "publicada" ? "activo" : "borrador";
  const estadoLabel = form.estado === "publicada" ? "Publicada" : "Borrador";

  /* Seis pestañas, no siete: `Previsualizar` desapareció porque la
     previsualización ahora es el panel de al lado. `badge` en Productos es la
     cuenta de filas — `YoolTabs` la dibuja como pill dentro de la pestaña
     (bundle: `y-tabs__badge`), así que se ve si hay productos sin entrar. */
  const opcionesTiempo = opcionesDeTiempo(form.tiempo_lectura);

  const TABS = [
    { value: "historia", label: "Historia" },
    { value: "portada", label: "Portada" },
    { value: "cuerpo", label: "Cuerpo" },
    { value: "medio", label: "Editorial intermedio" },
    { value: "caso", label: "Caso B2B" },
    { value: "productos", label: "Productos", badge: form.productos.length },
  ];


  /* Publicar sin producto principal se permite, pero no en silencio: la
     historia sale sin banda y sin botón de simular, y eso conviene decidirlo
     a la vista y no descubrirlo después en el sitio. El backend ya no lo
     bloquea —flow renderiza bien sin él, ver HistoriaShell—; la confirmación
     vive acá. */
  const guardar = useCallback(async ({ omitirAvisoPrincipal = false } = {}) => {
    const entrada = haciaEntrada(form);
    // Requisito fuera del brief: slugDesdeNombre puede devolver '' (título
    // solo con emoji o solo en otro alfabeto). El backend no lo valida —
    // sin slug la URL pública de la historia no existe — así que se corta
    // acá, para borrador y para publicada por igual.
    if (!entrada.slug) {
      toast.error(
        "Hace falta un slug para la URL pública. El título (o el slug escrito a mano) no tiene letras ni números; cámbialo antes de guardar."
      );
      return;
    }
    // C2: una fila de producto sin elegir mandaba producto_id: '' al backend.
    // El RPC fallaba DESPUÉS de insertar la historia (22P02), dejando una
    // historia fantasma que ocupaba el slug y trababa el siguiente intento
    // con un 409. Se corta acá, con el mismo criterio que el slug: nada de
    // productos a medias llega a la API.
    if (entrada.productos.some((p) => !p.producto_id)) {
      toast.error(
        "Hay una fila en Productos sin producto elegido. Complétala o quítala antes de guardar."
      );
      return;
    }
    // Publicar sin producto principal: se puede, pero se pregunta una vez.
    // Solo aplica al publicar — un borrador sin producto es lo normal.
    const sinPrincipal =
      entrada.estado === "publicada" &&
      !(entrada.productos || []).some((p) => p.principal);
    if (sinPrincipal && !omitirAvisoPrincipal) {
      setAvisoPrincipalAbierto(true);
      return;
    }

    setGuardando(true);
    try {
      const token = await getToken();
      if (editando) await actualizar(token, id, entrada);
      else await crear(token, entrada);
      toast.success("Historia guardada");
      navigate("/editorial/historias");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGuardando(false);
    }
  }, [form, editando, id, getToken, navigate]);

  // ── Cuerpo (bloques) ────────────────────────────────────────────────────
  const setBloque = (i, campo) => (e) =>
    setForm((f) => {
      const cuerpo = [...f.cuerpo];
      cuerpo[i] = { ...cuerpo[i], [campo]: e.target.value };
      return { ...f, cuerpo };
    });

  /* Las tarjetas de tipo emiten el VALOR, no un evento (igual que `CampoPills`
     y `FkRadioGroup`). Se envuelve en la forma que espera `setBloque` para que
     el bloque siga teniendo un solo camino de escritura. */
  const setBloqueValor = (i, campo) => (valor) =>
    setBloque(i, campo)({ target: { value: valor } });

  const moverBloque = (i, delta) =>
    setForm((f) => {
      const j = i + delta;
      if (j < 0 || j >= f.cuerpo.length) return f;
      const cuerpo = [...f.cuerpo];
      [cuerpo[i], cuerpo[j]] = [cuerpo[j], cuerpo[i]];
      return { ...f, cuerpo };
    });

  const quitarBloque = (i) =>
    setForm((f) => ({ ...f, cuerpo: f.cuerpo.filter((_, k) => k !== i) }));

  const agregarBloque = () =>
    setForm((f) => ({ ...f, cuerpo: [...f.cuerpo, { tipo: "parrafo", texto: "" }] }));

  // ── Stats (ficha "el caso en números", hasta 4 filas) ───────────────────
  const setStat = (i, campo) => (e) =>
    setForm((f) => {
      const stats = [...f.stats];
      stats[i] = { ...stats[i], [campo]: e.target.value };
      return { ...f, stats };
    });

  const quitarStat = (i) =>
    setForm((f) => ({ ...f, stats: f.stats.filter((_, k) => k !== i) }));

  const agregarStat = () =>
    setForm((f) => (f.stats.length >= 4 ? f : { ...f, stats: [...f.stats, { k: "", v: "" }] }));

  // ── Editorial intermedio (medio) — objeto único, no lista ───────────────
  const setMedio = (campo) => (e) =>
    setForm((f) => ({
      ...f,
      medio: { ...f.medio, [campo]: e?.target ? e.target.value : e },
    }));

  // ── Caso B2B — objeto único, no lista ────────────────────────────────────
  const setCaso = (campo) => (e) =>
    setForm((f) => ({ ...f, caso: { ...f.caso, [campo]: e.target.value } }));

  // ── Productos ────────────────────────────────────────────────────────────
  const setProductoCampo = (i, campo) => (e) =>
    setForm((f) => {
      const valor = e?.target ? e.target.value : e;
      const filas = [...f.productos];
      filas[i] = { ...filas[i], [campo]: valor };
      return { ...f, productos: filas };
    });

  // Un solo principal: marcarlo en una fila lo desmarca en las demás. Esto
  // es distinto del trigger de "destacada" (eso es entre historias, en la
  // base); acá es entre filas de la MISMA historia, y sí es responsabilidad
  // del formulario.
  const setPrincipal = (i) =>
    setForm((f) => ({
      ...f,
      productos: f.productos.map((p, j) => ({ ...p, principal: j === i })),
    }));

  const quitarProducto = (i) =>
    setForm((f) => ({ ...f, productos: f.productos.filter((_, j) => j !== i) }));

  const agregarProducto = () =>
    setForm((f) => ({
      ...f,
      productos: [
        ...f.productos,
        { producto_id: "", principal: f.productos.length === 0, orden: f.productos.length },
      ],
    }));

  // El backend rechaza producto_id repetido con 422 (red de seguridad), pero
  // la experiencia es que cada fila ni siquiera pueda ofrecer un producto
  // que ya eligió otra fila.
  //
  // I4: nunca se ofrece un producto archivado (estado terminal, mismo
  // criterio que ProductsList), y uno inactivo solo se ofrece si es el que
  // la propia fila ya tiene elegido — si no, la fila lo esconde en silencio
  // y el editor no puede ni ver cuál era ni cambiarlo.
  const opcionesProducto = (i) => {
    const propioId = form.productos[i]?.producto_id;
    const elegidosEnOtras = new Set(
      form.productos.filter((_, j) => j !== i).map((p) => p.producto_id).filter(Boolean)
    );
    return productos
      .filter((p) => p.archivado !== true)
      .filter((p) => p.activo !== false || p.id === propioId)
      .filter((p) => p.id === propioId || !elegidosEnOtras.has(p.id))
      .map((p) => ({ value: p.id, label: p.nombre }));
  };

  const setBanda = (campo) => (e) =>
    setForm((f) => ({
      ...f,
      producto_banda: { ...f.producto_banda, [campo]: e?.target ? e.target.value : e },
    }));

  // C1: mientras la historia real todavía está cargando, no se muestra nada
  // editable — ni siquiera con los valores por defecto.
  if (cargando) {
    return (
      <div>
        <PageHead title="Editar historia" onBack={() => navigate("/editorial/historias")} />
        <FormCard>
          <p>Cargando…</p>
        </FormCard>
      </div>
    );
  }

  // C1: si el GET falló, no hay formulario que editar ni botón de Guardar —
  // guardar acá habría sobreescrito la historia real con el formulario vacío.
  if (errorCarga) {
    return (
      <div>
        <PageHead title="Editar historia" onBack={() => navigate("/editorial/historias")} />
        <FormCard>
          <Callout tone="warn" title="No se pudo cargar la historia">
            {errorCarga}. No se puede editar hasta volver a cargarla — recarga la página o vuelve al listado.
          </Callout>
        </FormCard>
      </div>
    );
  }

  return (
    <div>
      {/* Encabezado del detalle. El "← Historias" va arriba y suelto, como en
          el template, en vez del `onBack` que PageHead dibuja pegado al
          título. */}
      <div style={{ display: "grid", gap: 16, marginBlockEnd: 24 }}>
        <div style={{ display: "flex" }}>
          <Boton variant="link" size="sm" onClick={() => navigate("/editorial/historias")}>
            ← Historias
          </Boton>
        </div>
        <div
          style={{
            display: "flex", alignItems: "flex-end", justifyContent: "space-between",
            gap: 24, flexWrap: "wrap",
          }}
        >
          {YoolPageHeader ? (
            <YoolPageHeader eyebrow={formEyebrow} title={formTitulo} subtitle={formSlugUrl} />
          ) : (
            <PageHead title={formTitulo} sub={formSlugUrl} />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {form.destacada && YoolBadge ? (
              <YoolBadge variant="recomendado">Destacada</YoolBadge>
            ) : null}
            {StatusPill ? (
              <StatusPill estado={pillEstado}>{estadoLabel}</StatusPill>
            ) : null}
            <Boton
              variant="ghost"
              size="sm"
              onClick={() => setPreviewAbierto((v) => !v)}
            >
              {previewAbierto ? "Ocultar previsualización" : "Mostrar previsualización"}
            </Boton>
          </div>
        </div>
      </div>

      <div className="ed-form">
        <div className="ed-form__main">
          {YoolTabs ? (
            <YoolTabs variant="underline" value={tab} options={TABS} onChange={setTab} />
          ) : (
            /* Sin bundle: una fila de botones. No se reimplementa el subrayado
               ni el badge — la pantalla sigue navegable, que es lo que importa. */
            <div role="tablist" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {TABS.map((t) => (
                <Boton
                  key={t.value}
                  variant={t.value === tab ? "primary" : "ghost"}
                  size="sm"
                  onClick={() => setTab(t.value)}
                >
                  {t.label}
                  {t.badge != null ? ` (${t.badge})` : ""}
                </Boton>
              ))}
            </div>
          )}

          <Tarjeta card={YoolCard}>

          <Panel value="historia" tab={tab}>
            {/* Dos secciones, como el template: "Identidad" (cómo se llama y
                en qué URL vive) y "Publicación" (cuándo aparece y dónde). Antes
                era una sola con los ocho campos seguidos. */}
            <Section title="Identidad" desc="Cómo se llama la historia y en qué URL vive." first>
              <Pila>
                <Fila>
                  <CampoTexto label="Título" id="nombre" name="nombre" required
                    value={form.nombre} onChange={set("nombre")}
                    placeholder="Bárbara contrató Complementario en 8 minutos" />
                  {/* El slug se propone con un botón explícito, no al salir del
                      campo. El onBlur solo actuaba si el slug seguía vacío, así
                      que era invisible y no repetible: renombrar la historia
                      dejaba el slug viejo sin forma de regenerarlo. */}
                  <div style={{ display: "grid", gap: 8 }}>
                    <CampoTexto label="Slug" id="slug" name="slug" required full
                      hint="Define la URL pública: sin tildes, sin signos, separado por guiones."
                      value={form.slug} onChange={set("slug")}
                      placeholder="barbara-complementario" />
                    <div style={{ display: "flex", justifyContent: "flex-start" }}>
                      <Boton variant="link" size="sm"
                        onClick={() => setForm((f) => ({ ...f, slug: slugDesdeNombre(f.nombre) }))}>
                        Proponer desde el título
                      </Boton>
                    </div>
                  </div>
                </Fila>
                {/* Pills, no desplegable: dos valores que conviene tener a la
                    vista. El template pide aquí `YoolTabs variant="segmented"`;
                    la decisión de usar pills con punto FancyYool es posterior y
                    está explicada en `CampoPills` (campos.jsx). */}
                <Fila>
                  <CampoPills label="Estado" id="estado"
                    value={form.estado}
                    onChange={(v) => setForm((f) => ({ ...f, estado: v }))}
                    options={[
                      { value: "borrador", label: "Borrador" },
                      { value: "publicada", label: "Publicada" },
                    ]} />
                  <CampoPills label="Categoría" id="categoria"
                    value={form.categoria}
                    onChange={(v) => setForm((f) => ({ ...f, categoria: v }))}
                    options={CATEGORIAS.map((c) => ({ value: c.valor, label: c.etiqueta }))} />
                </Fila>
              </Pila>
            </Section>

            <Section title="Publicación" desc="Cuándo aparece y en qué lugar de la grilla del home.">
              <Pila>
                <Fila reparto="3">
                  {/* Stepper y no campo libre: es lo que impide de raíz el `orden`
                      decimal o con texto que reventaba el guardado dejando una
                      historia fantasma. Ver la nota en campos.jsx. */}
                  <CampoNumero label="Orden en la grilla" id="orden"
                    hint="Vacío queda al final." value={form.orden}
                    onChange={set("orden")} min={1} />
                  <CampoFecha label="Fecha de publicación" id="fecha_publicacion"
                    name="fecha_publicacion" value={form.fecha_publicacion}
                    onChange={set("fecha_publicacion")} />
                  {/* Menú y no texto libre: el valor va tal cual a la tarjeta y
                      al hero del sitio, así que "3 min de lectura", "3 minutos"
                      y "3min" se veían como tres formatos en la misma grilla. */}
                  <CampoSelect label="Tiempo de lectura" id="tiempo_lectura"
                    name="tiempo_lectura" value={form.tiempo_lectura}
                    onChange={set("tiempo_lectura")} options={opcionesTiempo} />
                </Fila>
                <CampoSwitch id="destacada" label="Destacada en el home"
                  desc="Ocupa el FeaturedStory del home. Marcar una nueva desmarca la anterior — lo hace un trigger en la base, no este formulario."
                  checked={form.destacada}
                  onChange={(on) => setForm((f) => ({ ...f, destacada: on }))} />
              </Pila>
            </Section>
          </Panel>

          <Panel value="portada" tab={tab}>
            <Section title="Portada" desc="Es el hero de la página y la tarjeta en el home." first>
              <Pila>
                <Fila>
                  <CampoTexto label="Eyebrow" id="eyebrow" name="eyebrow" value={form.eyebrow} onChange={set("eyebrow")} placeholder="Caso destacado · Salud" />
                  {/* I2: la opción vacía va DENTRO de `options`, no por `placeholder`
                      — formkit dibuja la opción de `placeholder` deshabilitada, y acá
                      "sin resaltado" es un valor válido al que hay que poder volver. */}
                  {/* Muestras del color, no nombres de token: el editor elegia
                      "dark-cyan" de una lista y tenia que traducirlo de memoria. */}
                  <CampoColor label="Color del resaltado"
                    value={form.highlight_color} onChange={set("highlight_color")}
                    options={COLORES_FANCY} />
                </Fila>
                <CampoArea label="Cita" required full id="quote" name="quote" value={form.quote} onChange={set("quote")} rows={3} />
                <CampoResaltados
                  hint="Cada término se busca dentro de la cita, sin distinguir mayúsculas, y se resaltan todas sus apariciones."
                  idBase="highlight" texto={form.quote} colores={COLORES_FANCY}
                  value={form.highlight} onChange={set("highlight")} />
                <CampoArea label="Bajada" full id="dek" name="dek" value={form.dek} onChange={set("dek")} rows={3} />
                <CampoImagen required
                  urlValue={form.imagen_url} onUrlChange={set("imagen_url")}
                  posValue={form.imagen_posicion} onPosChange={set("imagen_posicion")} />
              </Pila>
            </Section>
          </Panel>

          <Panel value="cuerpo" tab={tab}>
            <Section title="Cuerpo" desc="Los bloques se renderizan en este orden." first>
              {/* Tarjeta y no fila: con los campos y los tres botones en un
                  mismo `flex-wrap`, "Quitar" de un bloque quedaba pegado al
                  "Tipo" del siguiente y no se veia donde termina cada unidad. */}
              {form.cuerpo.map((bloque, i) => (
                <TarjetaRepetidor key={i} etiqueta="Bloque" numero={i + 1}
                  acciones={
                    <>
                      <Boton variant="ghost" size="sm" disabled={i === 0}
                        onClick={() => moverBloque(i, -1)}>Subir</Boton>
                      <Boton variant="ghost" size="sm" disabled={i === form.cuerpo.length - 1}
                        onClick={() => moverBloque(i, 1)}>Bajar</Boton>
                      <Boton variant="danger-outline" size="sm"
                        onClick={() => quitarBloque(i)}>Quitar</Boton>
                    </>
                  }>
                  {/* Tarjetas y no desplegable: los tres nombres no dicen cómo
                      queda el bloque publicado, y el `select` obligaba a abrir
                      la lista para ver qué había. La tarjeta muestra las tres a
                      la vez CON su explicación, que es lo que las pills no
                      tienen dónde poner. */}
                  <CampoRadioCards label="Tipo" id={`tipo-${i}`} columns={3}
                    value={bloque.tipo} onChange={setBloqueValor(i, "tipo")}
                    options={TIPOS_BLOQUE.map((t) => ({
                      value: t.valor, title: t.etiqueta, desc: t.descripcion,
                    }))} />
                  <CampoArea label="Texto" htmlFor={`texto-${i}`} full id={`texto-${i}`} value={bloque.texto} onChange={setBloque(i, "texto")} rows={4} />
                </TarjetaRepetidor>
              ))}
              <div style={{ marginBlockStart: 16 }}>
                <Boton variant="outline" size="sm" onClick={agregarBloque}>Agregar bloque</Boton>
              </div>
            </Section>

            <Section title="El caso en números" desc="Ficha opcional, hasta 4 filas. Vacía, no se muestra.">
              {form.stats.map((stat, i) => (
                <TarjetaRepetidor key={i} etiqueta="Fila" numero={i + 1}
                  acciones={
                    <Boton variant="danger-outline" size="sm"
                      onClick={() => quitarStat(i)}>Quitar</Boton>
                  }>
                  <Fila>
                    <CampoTexto label="Etiqueta" htmlFor={`stat-k-${i}`} id={`stat-k-${i}`} value={stat.k} onChange={setStat(i, "k")} placeholder="Tiempo hasta el plan activo" />
                    <CampoTexto label="Valor" htmlFor={`stat-v-${i}`} id={`stat-v-${i}`} value={stat.v} onChange={setStat(i, "v")} placeholder="58 min" />
                  </Fila>
                </TarjetaRepetidor>
              ))}
              {form.stats.length < 4 && (
                <div style={{ marginBlockStart: 16 }}>
                  <Boton variant="outline" size="sm" onClick={agregarStat}>Agregar stat</Boton>
                </div>
              )}
            </Section>
          </Panel>

          <Panel value="medio" tab={tab}>
            <Section title="Editorial intermedio" desc="Bloque EditorialSplit entre el cuerpo y la banda de producto." first>
              <Callout tone="info">
                Dejar todos estos campos vacíos oculta el bloque completo en la página; no hace falta borrar nada aparte.
              </Callout>
              <Pila>
                <Fila>
                  <CampoTexto label="Eyebrow" id="medio-eyebrow" value={form.medio.eyebrow} onChange={setMedio("eyebrow")} />
                  <CampoColor label="Color del resaltado"
                    value={form.medio.highlight_color} onChange={setMedio("highlight_color")}
                    options={COLORES_FANCY} />
                </Fila>
                <CampoTexto label="Título" full id="medio-titulo" value={form.medio.titulo} onChange={setMedio("titulo")} />
                <CampoResaltados
                  idBase="medio-highlight" texto={form.medio.titulo} colores={COLORES_FANCY}
                  value={form.medio.highlight} onChange={setMedio("highlight")} />
                <CampoArea label="Texto" full id="medio-texto" value={form.medio.texto} onChange={setMedio("texto")} rows={4} />
                {/* Mismo campo compuesto que la portada: URL, encuadre y vista
                    previa. Antes era una URL suelta, así que el `EditorialSplit`
                    recortaba la foto por el centro sin forma de moverla. */}
                <CampoImagen
                  idUrl="medio-imagen_url" idPos="medio-imagen_posicion"
                  urlValue={form.medio.imagen_url} onUrlChange={setMedio("imagen_url")}
                  posValue={form.medio.imagen_posicion} onPosChange={setMedio("imagen_posicion")} />
              </Pila>
            </Section>
          </Panel>

          <Panel value="caso" tab={tab}>
            <Section title="Caso B2B" desc="Los cinco campos que consume /business." first>
              <Callout tone="info">
                "Antes" y "Después" se dibujan cuando esta historia es el caso destacado; "Problema",
                "Solución" y "Resultado" se dibujan cuando es una tarjeta de apoyo. Dejar los cinco
                campos vacíos significa que la historia no es un caso B2B, y los bloques sin texto no
                se dibujan — así que alcanza con llenar solo los que correspondan según cuál de los dos
                usos tenga esta historia.
              </Callout>
              <Pila>
                <CampoArea label="Antes" full hint="Cuadro izquierdo del panel del caso destacado." id="caso-antes" value={form.caso.antes} onChange={setCaso("antes")} rows={3} />
                <CampoArea label="Después" full hint="Cuadro derecho, el que se resalta." id="caso-despues" value={form.caso.despues} onChange={setCaso("despues")} rows={3} />
                <CampoArea label="Problema" full hint="Primer bloque de la tarjeta de apoyo." id="caso-problema" value={form.caso.problema} onChange={setCaso("problema")} rows={3} />
                <CampoArea label="Solución" full hint="Segundo bloque." id="caso-solucion" value={form.caso.solucion} onChange={setCaso("solucion")} rows={3} />
                <CampoArea label="Resultado" full hint="Tercer bloque, el que se destaca en color." id="caso-resultado" value={form.caso.resultado} onChange={setCaso("resultado")} rows={3} />
              </Pila>
            </Section>
          </Panel>

          <Panel value="productos" tab={tab}>
            <Section title="Productos" desc="Chips secundarios y disparo de simulación desde la historia." first>
              <Callout tone="info">
                El principal es el producto que dispara el caso o que da el titular; si ninguno destaca
                claramente, el de mayor prima.
              </Callout>
              {form.productos.map((fila, i) => (
                <TarjetaRepetidor key={i} etiqueta="Producto" numero={i + 1}
                  acciones={
                    <Boton variant="danger-outline" size="sm"
                      onClick={() => quitarProducto(i)}>Quitar</Boton>
                  }>
                  <Fila reparto="3">
                    <CampoSelect label="Producto" htmlFor={`producto-${i}`} id={`producto-${i}`} value={fila.producto_id} onChange={setProductoCampo(i, "producto_id")} placeholder="Elegir producto" options={opcionesProducto(i)} />
                    <CampoTexto label="Orden" htmlFor={`producto-orden-${i}`} id={`producto-orden-${i}`} type="number" value={fila.orden} onChange={setProductoCampo(i, "orden")} />
                    <Field label="Principal" htmlFor={`producto-principal-${i}`}>
                      <div style={{ display: "flex", alignItems: "center", height: 40 }}>
                        <input type="radio" id={`producto-principal-${i}`} name="producto-principal"
                          checked={Boolean(fila.principal)} onChange={() => setPrincipal(i)} />
                      </div>
                    </Field>
                  </Fila>
                </TarjetaRepetidor>
              ))}
              <div style={{ marginBlockStart: 16 }}>
                <Boton variant="outline" size="sm" onClick={agregarProducto}>Agregar producto</Boton>
              </div>
            </Section>

            <Section title="Banda del producto principal" desc="Overrides opcionales; lo que no se llene sale del catálogo.">
              <Pila>
                <Fila>
                  <CampoTexto label="Eyebrow" id="banda-eyebrow" value={form.producto_banda.eyebrow} onChange={setBanda("eyebrow")} />
                  <CampoColor label="Color del resaltado"
                    value={form.producto_banda.highlight_color} onChange={setBanda("highlight_color")}
                    options={COLORES_FANCY} />
                </Fila>
                <CampoTexto label="Título" full id="banda-titulo" value={form.producto_banda.titulo} onChange={setBanda("titulo")} />
                <CampoTexto label="Subtítulo" full id="banda-subtitulo" value={form.producto_banda.subtitulo} onChange={setBanda("subtitulo")} />
                <CampoResaltados
                  idBase="banda-highlight" texto={form.producto_banda.titulo} colores={COLORES_FANCY}
                  value={form.producto_banda.highlight} onChange={setBanda("highlight")} />
                {/* El par URL + posición ya no es una fila de dos campos de
                    texto: es el mismo `CampoImagen` de la portada, con la
                    grilla de encuadre y la vista previa al lado. */}
                <CampoImagen
                  idUrl="banda-imagen_url" idPos="banda-imagen_posicion"
                  urlValue={form.producto_banda.imagen_url} onUrlChange={setBanda("imagen_url")}
                  posValue={form.producto_banda.imagen_posicion} onPosChange={setBanda("imagen_posicion")} />
              </Pila>
            </Section>
          </Panel>

          </Tarjeta>
        </div>

        {/* Panel de previsualización. `Previsualizacion` compone las mismas
            vistas de siempre, así que sigue habiendo un solo lugar donde se
            define cómo se dibuja cada sección. Lo nuevo es DÓNDE se mira: al
            lado de los campos y pegado al hacer scroll, no en una pestaña
            aparte a la que había que ir a propósito. */}
        {previewAbierto ? (
          <aside className="ed-form__preview" aria-label="Previsualización de la historia">
            <Tarjeta card={YoolCard} pad={28}>
              <Previsualizacion form={form} productos={productos} />
            </Tarjeta>
          </aside>
        ) : null}
      </div>

      <div>
        {/* Barra fija abajo: en un formulario de siete pestañas el botón de
            guardar quedaba fuera de alcance al hacer scroll. A la izquierda va
            el estado, para que "sin cambios" y "cambios sin guardar" no se
            distingan solo por memoria.

            El handoff describe tres estados y acá solo hay dos: "Cambios
            guardados" no se dibuja nunca porque al guardar se navega al
            listado, así que ese estado no tiene dónde verse. Prometerlo sería
            texto muerto. */}
        {YoolActionBar ? (
          <YoolActionBar
            sticky
            leading={
              <span
                style={{
                  fontSize: 13,
                  color: sinGuardar
                    ? "var(--yool-black, #1A1A1A)"
                    : "var(--yool-muted, #8D8D8D)",
                  fontWeight: sinGuardar ? 600 : 400,
                }}
              >
                {sinGuardar ? "Cambios sin guardar" : "Sin cambios"}
              </span>
            }
          >
            <Boton variant="ghost" onClick={() => navigate("/editorial/historias")}>
              Cancelar
            </Boton>
            <Boton variant="primary" onClick={guardar} disabled={guardando}>
              {guardando ? "Guardando…" : "Guardar historia"}
            </Boton>
          </YoolActionBar>
        ) : (
          <ActionBar
            left={
              <Boton variant="ghost" onClick={() => navigate("/editorial/historias")}>
                Cancelar
              </Boton>
            }
            right={
              <Boton variant="primary" onClick={() => guardar()} disabled={guardando}>
                {guardando ? "Guardando…" : "Guardar historia"}
              </Boton>
            }
          />
        )}
      </div>

      {/* Publicar sin producto principal. No es un error —flow lo renderiza
          bien, con el aside ajustado— pero cambia lo que ve el lector, así que
          se decide a la vista y una sola vez. */}
      <AlertDialog
        open={avisoPrincipalAbierto}
        onOpenChange={setAvisoPrincipalAbierto}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publicar sin producto principal</AlertDialogTitle>
            <AlertDialogDescription>
              La historia se va a publicar sin banda de producto y sin botón de
              simular. Se lee completa igual; lo que no tendrá es la salida
              hacia un producto. Puedes marcar un principal en la pestaña
              Productos, o continuar así.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver y marcar uno</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => guardar({ omitirAvisoPrincipal: true })}
            >
              Publicar sin producto
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
